import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { Article as ArticleIcon } from '@mui/icons-material';
import BlogForm from './BlogForm';
import { FormPageLayout } from '../../components/templates';
import { getBlogBySlug, createBlog, updateBlog, getAllBlogs } from '../../api/blogApi';
import { useToast } from '../../contexts/ToastContext';

const BlogFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(Boolean(id));
  const [saving, setSaving] = useState(false);
  const [initialData, setInitialData] = useState({});
  const isEditMode = Boolean(id);

  useEffect(() => {
    if (id) {
      const fetchBlog = async () => {
        try {
          setLoading(true);
          const { data } = await getAllBlogs();
          const blog = data.blogs?.find((b) => b._id === id);
          if (blog) {
            const { data: fullData } = await getBlogBySlug(blog.slug);
            setInitialData(fullData.blog || fullData);
          }
        } catch (error) {
          console.error('Failed to fetch blog:', error);
          showToast('Failed to load blog', 'error');
        } finally {
          setLoading(false);
        }
      };
      fetchBlog();
    }
  }, [id, showToast]);

  const handleSubmit = async (formData) => {
    try {
      setSaving(true);
      if (isEditMode) {
        await updateBlog(id, formData);
        showToast('Blog updated successfully!', 'success');
      } else {
        await createBlog(formData);
        showToast('Blog created successfully!', 'success');
      }
      navigate('/blogs');
    } catch (error) {
      console.error('Failed to save blog:', error);
      showToast('Failed to save blog', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <FormPageLayout
      title={isEditMode ? 'Edit Blog Post' : 'Create Blog Post'}
      subtitle={isEditMode ? 'Update blog post content and settings' : 'Create a new blog post'}
      icon={<ArticleIcon />}
      backPath="/blogs"
      backLabel="Back to Blogs"
      maxWidth="xl"
    >
      <BlogForm
        key={initialData._id || 'new'}
        initialData={initialData}
        onSubmit={handleSubmit}
        loading={saving}
      />
    </FormPageLayout>
  );
};

export default BlogFormPage;
